import React from 'react';

export type SkeletonVariant = 'text' | 'circular' | 'rectangular' | 'rounded';

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: SkeletonVariant;
  width?: string | number;
  height?: string | number;
  className?: string;
}

export const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
  ({ variant = 'text', width, height, className = '', style, ...props }, ref) => {
    const variantClasses: Record<SkeletonVariant, string> = {
      text: 'rounded h-4',
      circular: 'rounded-full',
      rectangular: 'rounded-none',
      rounded: 'rounded-xl'
    };

    const inlineStyles: React.CSSProperties = {
      ...(width !== undefined ? { width: typeof width === 'number' ? `${width}px` : width } : {}),
      ...(height !== undefined ? { height: typeof height === 'number' ? `${height}px` : height } : {}),
      ...style
    };

    return (
      <div
        ref={ref}
        aria-hidden="true"
        className={`animate-pulse bg-base-300 ${variantClasses[variant]} ${className}`}
        style={inlineStyles}
        {...props}
      />
    );
  }
);

Skeleton.displayName = 'Skeleton';

export interface SkeletonStatProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
}

export const SkeletonStat: React.FC<SkeletonStatProps> = ({ className = '', ...props }) => {
  return (
    <div
      aria-hidden="true"
      className={`bg-base-200 border border-base-300 flex flex-col justify-between gap-2 p-3.5 md:px-5 md:py-[18px] rounded-[14px] md:rounded-2xl min-h-[92px] md:min-h-[114px] ${className}`}
      {...props}
    >
      <div className="flex items-center justify-between">
        <Skeleton variant="text" className="w-20 md:w-24 h-3.5" />
        <Skeleton variant="circular" className="size-3.5 md:size-4" />
      </div>
      <div className="flex items-baseline gap-2">
        <Skeleton variant="rounded" className="w-16 md:w-24 h-6 md:h-8" />
        <Skeleton variant="text" className="w-8 h-3" />
      </div>
      <div className="hidden md:flex items-center gap-2">
        <Skeleton variant="text" className="w-28 h-3" />
      </div>
    </div>
  );
};

SkeletonStat.displayName = 'SkeletonStat';

export interface SkeletonCardProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  children?: React.ReactNode;
}

export const SkeletonCard: React.FC<SkeletonCardProps> = ({
  className = '',
  children,
  ...props
}) => {
  return (
    <div
      aria-hidden="true"
      className={`bg-base-200 border border-base-300 rounded-2xl p-4 md:p-5 lg:p-6 ${className}`}
      {...props}
    >
      {children || (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Skeleton variant="text" className="w-32 h-5" />
            <Skeleton variant="rounded" className="w-16 h-4" />
          </div>
          <Skeleton variant="rounded" className="w-full h-24" />
          <div className="space-y-2">
            <Skeleton variant="text" className="w-3/4 h-4" />
            <Skeleton variant="text" className="w-1/2 h-3" />
          </div>
        </div>
      )}
    </div>
  );
};

SkeletonCard.displayName = 'SkeletonCard';

export interface SkeletonRowProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  hasLeading?: boolean;
  hasTrailing?: boolean;
}

export const SkeletonRow: React.FC<SkeletonRowProps> = ({
  className = '',
  hasLeading = true,
  hasTrailing = true,
  ...props
}) => {
  return (
    <div
      aria-hidden="true"
      className={`flex items-center gap-3.5 py-3 px-3.5 md:p-3.5 lg:py-3.5 lg:px-5 min-h-[48px] ${className}`}
      {...props}
    >
      {hasLeading && (
        <Skeleton
          variant="rounded"
          className="size-10 md:size-9 rounded-xl md:rounded-[10px] shrink-0"
        />
      )}
      <div className="flex-1 min-w-0 space-y-1.5">
        <Skeleton variant="text" className="w-2/5 md:w-1/3 h-4" />
        <Skeleton variant="text" className="w-1/4 h-3" />
      </div>
      {hasTrailing && (
        <Skeleton
          variant="rounded"
          className="size-8 md:size-9 rounded-xl md:rounded-[10px] shrink-0"
        />
      )}
    </div>
  );
};

SkeletonRow.displayName = 'SkeletonRow';

export interface SkeletonChartProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
}

export const SkeletonChart: React.FC<SkeletonChartProps> = ({ className = '', ...props }) => {
  const barHeights = [45, 60, 25, 75, 40, 50, 65];

  return (
    <section
      aria-hidden="true"
      className={`bg-base-200 border border-base-300 rounded-2xl p-4 md:p-5 h-full flex flex-col justify-between gap-3 md:gap-4 ${className}`}
      {...props}
    >
      <div className="flex items-center justify-between">
        <Skeleton variant="text" className="w-16 md:w-20 h-4" />
        <Skeleton variant="text" className="w-16 h-3" />
      </div>

      <div className="h-[110px] md:h-[150px] flex items-end gap-2 md:gap-2.5 pt-4">
        {barHeights.map((heightPercent, idx) => (
          <div
            key={idx}
            className="flex-1 h-full flex flex-col items-center justify-end gap-1.5"
          >
            <div
              style={{ height: `${heightPercent}%` }}
              className="w-full rounded-t md:rounded-t-md rounded-b-[2px] bg-base-300 animate-pulse"
            />
            <Skeleton variant="text" className="w-4 h-3" />
          </div>
        ))}
      </div>
    </section>
  );
};

SkeletonChart.displayName = 'SkeletonChart';
